import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Calendar, MapPin, Clock, Heart, Briefcase, Target } from 'lucide-react';
import { BR_STATES, isBrazil } from '../data/brazilStates';
import { useBrazilCities } from '../hooks/useBrazilCities';
import BirthTimeSelect, { isPartialTime } from './BirthTimeSelect';

interface UserRegistrationData {
  // Dados básicos
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  
  // Dados do perfil
  birthDate: string;
  gender: string;
  birthCity: string;
  birthState?: string;
  birthCountry: string;
  
  // Dados opcionais para mapa astral
  birthTime?: string;
  birthLatitude?: number;
  birthLongitude?: number;
  birthTimezone?: string;
  
  // Informações complementares
  maritalStatus?: string;
  profession?: string;
  mainInterests: string[];
  currentGoals: string[];
}

interface UserRegistrationFormProps {
  onSubmit: (data: UserRegistrationData) => Promise<void>;
  loading?: boolean;
  onAnyChange?: () => void;
}

const UserRegistrationForm: React.FC<UserRegistrationFormProps> = ({ onSubmit, loading = false, onAnyChange }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<UserRegistrationData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    gender: '',
    birthCity: '',
    birthCountry: 'Brasil',
    mainInterests: [],
    currentGoals: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // Select de cidade por UF (evita erro de digitação que atrapalha a geocodificação
  // do mapa astral) — só disponível pra Brasil; fora disso cai pra texto livre.
  const brazilForm = isBrazil(formData.birthCountry);
  const { cities: brCities, loading: citiesLoading, failed: citiesFailed } = useBrazilCities(
    brazilForm ? formData.birthState : null
  );
  const showCitySelect = brazilForm && !!formData.birthState && !citiesFailed;

  const genderOptions = [
    { value: 'masculino', label: 'Masculino' },
    { value: 'feminino', label: 'Feminino' },
    { value: 'não-binário', label: 'Não-binário' },
    { value: 'prefiro não dizer', label: 'Prefiro não dizer' }
  ];

  const maritalStatusOptions = [
    { value: 'solteiro', label: 'Solteiro(a)' },
    { value: 'namorando', label: 'Namorando' },
    { value: 'casado', label: 'Casado(a)' },
    { value: 'divorciado', label: 'Divorciado(a)' },
    { value: 'viúvo', label: 'Viúvo(a)' },
    { value: 'união estável', label: 'União Estável' }
  ];

  const interestOptions = [
    'Astrologia', 'Tarot', 'Numerologia', 'Meditação', 'Espiritualidade',
    'Autoconhecimento', 'Relacionamentos', 'Carreira', 'Saúde', 'Finanças'
  ];

  const goalOptions = [
    'Autoconhecimento', 'Melhorar relacionamentos', 'Crescimento profissional',
    'Saúde e bem-estar', 'Estabilidade financeira', 'Desenvolvimento espiritual',
    'Encontrar propósito', 'Superar desafios'
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
      else if (formData.name.trim().split(/\s+/).filter(Boolean).length < 2) {
        newErrors.name = 'Digite seu nome completo (nome e sobrenome) — a numerologia usa todas as letras do seu nome.';
      }
      if (!formData.email.trim()) newErrors.email = 'Email é obrigatório';
      if (!formData.password) newErrors.password = 'Senha é obrigatória';
      if (formData.password.length < 6) newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Senhas não coincidem';
      }
    }

    if (step === 2) {
      if (!formData.birthDate) newErrors.birthDate = 'Data de nascimento é obrigatória';
      if (!formData.gender) newErrors.gender = 'Gênero é obrigatório';
      if (!formData.birthCity.trim()) newErrors.birthCity = 'Cidade de nascimento é obrigatória';
    }

    if (step === 3) {
      if (isPartialTime(formData.birthTime || '')) {
        newErrors.birthTime = 'Selecione a hora e o minuto do nascimento, ou deixe os dois em branco.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedPrivacy) {
      setErrors(prev => ({ ...prev, privacy: 'Você precisa aceitar a Política de Privacidade para continuar' }));
      return;
    }
    if (validateStep(currentStep)) {
      await onSubmit(formData);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (currentStep < 4) {
        if (validateStep(currentStep)) {
          setCurrentStep(currentStep + 1);
        }
      }
    }
  };

  const handleInputChange = (field: keyof UserRegistrationData, value: any) => {
    if (onAnyChange) onAnyChange();
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleArrayChange = (field: 'mainInterests' | 'currentGoals', value: string) => {
    if (onAnyChange) onAnyChange();
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Informações Básicas</h2>
        <p className="text-purple-200">Vamos começar com seus dados pessoais</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <User className="inline w-4 h-4 mr-2" />
            Nome Completo
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Nome e sobrenome"
          />
          <p className="text-xs text-purple-300 mt-1">
            Nome e sobrenome completos, como na certidão — usado no cálculo da numerologia.
          </p>
          {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Mail className="inline w-4 h-4 mr-2" />
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="seu@email.com"
          />
          {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Lock className="inline w-4 h-4 mr-2" />
            Senha
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Mínimo 6 caracteres"
          />
          {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Lock className="inline w-4 h-4 mr-2" />
            Confirmar Senha
          </label>
          <input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Digite a senha novamente"
          />
          {errors.confirmPassword && <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>}
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Informações Astrológicas</h2>
        <p className="text-purple-200">Dados necessários para calcular seu signo</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Calendar className="inline w-4 h-4 mr-2" />
            Data de Nascimento
          </label>
          <input
            type="date"
            value={formData.birthDate}
            onChange={(e) => handleInputChange('birthDate', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {errors.birthDate && <p className="text-red-400 text-sm mt-1">{errors.birthDate}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            Gênero
          </label>
          <select
            value={formData.gender}
            onChange={(e) => handleInputChange('gender', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Selecione seu gênero</option>
            {genderOptions.map(option => (
              <option key={option.value} value={option.value} className="bg-purple-900">
                {option.label}
              </option>
            ))}
          </select>
          {errors.gender && <p className="text-red-400 text-sm mt-1">{errors.gender}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            País de Nascimento
          </label>
          <input
            type="text"
            value={formData.birthCountry}
            onChange={(e) => handleInputChange('birthCountry', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Brasil"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            Estado {brazilForm ? '(UF)' : ''}
          </label>
          {brazilForm ? (
            <select
              value={formData.birthState || ''}
              onChange={(e) => {
                handleInputChange('birthState', e.target.value);
                handleInputChange('birthCity', ''); // cidade da UF anterior não vale mais
              }}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="" className="bg-purple-900">Selecione o estado</option>
              {BR_STATES.map(s => (
                <option key={s.uf} value={s.uf} className="bg-purple-900">{s.uf} — {s.name}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={formData.birthState || ''}
              onChange={(e) => handleInputChange('birthState', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Estado/província"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <MapPin className="inline w-4 h-4 mr-2" />
            Cidade de Nascimento
          </label>
          {showCitySelect ? (
            <select
              value={formData.birthCity}
              onChange={(e) => handleInputChange('birthCity', e.target.value)}
              disabled={citiesLoading}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60"
            >
              <option value="" className="bg-purple-900">
                {citiesLoading ? 'Carregando cidades…' : 'Selecione a cidade'}
              </option>
              {brCities.map(c => (
                <option key={c} value={c} className="bg-purple-900">{c}</option>
              ))}
            </select>
          ) : (
            <>
              <input
                type="text"
                value={formData.birthCity}
                onChange={(e) => handleInputChange('birthCity', e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={brazilForm ? 'Selecione o estado acima primeiro' : 'Ex: São Paulo'}
                disabled={brazilForm && !formData.birthState}
              />
              {citiesFailed && (
                <p className="text-amber-300 text-xs mt-1">Não foi possível carregar a lista de cidades agora — digite manualmente.</p>
              )}
            </>
          )}
          {errors.birthCity && <p className="text-red-400 text-sm mt-1">{errors.birthCity}</p>}
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Mapa Astral (Opcional)</h2>
        <p className="text-purple-200">Para um mapa astral completo, precisamos do horário exato</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Clock className="inline w-4 h-4 mr-2" />
            Horário de Nascimento (Opcional)
          </label>
          <BirthTimeSelect
            value={formData.birthTime || ''}
            onChange={(v) => handleInputChange('birthTime', v)}
            selectClassName="w-full px-3 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            optionStyle={{ background: '#2e1065' }}
            invalid={!!errors.birthTime}
          />
          <p className="text-xs text-purple-300 mt-1">
            Formato 24h. Se não souber o horário exato, pode deixar em branco.
          </p>
          {errors.birthTime && <p className="text-red-400 text-sm mt-1">{errors.birthTime}</p>}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-3">
          <p className="text-xs text-purple-200">
            A IA resolve automaticamente a Latitude/Longitude e o Fuso Horário a partir de Cidade + Estado (UF).
            Basta informar seu local; não é necessário informar coordenadas ou timezone.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Heart className="inline w-4 h-4 mr-2" />
            Estado Civil (Opcional)
          </label>
          <select
            value={formData.maritalStatus || ''}
            onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Selecione (opcional)</option>
            {maritalStatusOptions.map(option => (
              <option key={option.value} value={option.value} className="bg-purple-900">
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            <Briefcase className="inline w-4 h-4 mr-2" />
            Profissão (Opcional)
          </label>
          <input
            type="text"
            value={formData.profession || ''}
            onChange={(e) => handleInputChange('profession', e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Ex: Designer, Advogado, Estudante..."
          />
        </div>
      </div>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Interesses e Objetivos</h2>
        <p className="text-purple-200">Ajude-nos a personalizar sua experiência</p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-3">
            Principais Interesses (Opcional)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {interestOptions.map(interest => (
              <label key={interest} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.mainInterests.includes(interest)}
                  onChange={() => handleArrayChange('mainInterests', interest)}
                  className="rounded border-white/20 bg-white/10 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-sm text-purple-200">{interest}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-3">
            <Target className="inline w-4 h-4 mr-2" />
            Objetivos Atuais (Opcional)
          </label>
          <div className="grid grid-cols-1 gap-2">
            {goalOptions.map(goal => (
              <label key={goal} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.currentGoals.includes(goal)}
                  onChange={() => handleArrayChange('currentGoals', goal)}
                  className="rounded border-white/20 bg-white/10 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-sm text-purple-200">{goal}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-white/10">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedPrivacy}
              onChange={e => {
                setAcceptedPrivacy(e.target.checked);
                if (e.target.checked) setErrors(prev => { const n = { ...prev }; delete n.privacy; return n; });
              }}
              className="mt-0.5 rounded border-white/20 bg-white/10 text-purple-500 focus:ring-purple-500 flex-shrink-0"
            />
            <span className="text-sm text-purple-200">
              Li e aceito a{' '}
              <a
                href="/politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-300 hover:text-white underline"
              >
                Política de Privacidade
              </a>
              {' '}e autorizo o tratamento dos meus dados conforme descrito.
            </span>
          </label>
          {errors.privacy && (
            <p className="mt-2 text-xs text-red-400">{errors.privacy}</p>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="max-w-md mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-purple-200">Passo {currentStep} de 4</span>
          <span className="text-sm text-purple-200">{Math.round((currentStep / 4) * 100)}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Anterior
            </button>
          )}
          
          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="ml-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
            >
              Próximo
            </button>
          ) : (
            <div className="ml-auto flex flex-col items-end space-y-2">
              <button
                type="submit"
                disabled={loading || !acceptedPrivacy}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Criando conta...' : 'Criar Conta'}
              </button>
              {formData.mainInterests.length === 0 && formData.currentGoals.length === 0 && (
                <span className="text-xs text-purple-200">Selecione pelo menos um interesse ou objetivo (opcional, mas recomendado)</span>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default UserRegistrationForm;